import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import loreCornerDetail from '../../assets/lore/lore_details.png';
import loreButton from '../../assets/lore/lore_button.png';

function formatRarity(r) {
  const v = String(r || '').toLowerCase();
  if (v === 'rare') return 'RARE';
  if (v === 'uncommon') return 'UNCOMMON';
  if (v === 'common') return 'COMMON';
  return v ? v.toUpperCase() : '';
}

function CornerDetail({ className = '', rotationClass = '' }) {
  return (
    <img
      src={loreCornerDetail}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={`pointer-events-none absolute h-[52px] w-[52px] select-none opacity-80 ${rotationClass} ${className}`}
    />
  );
}

export function RelicPickerModal({
  open,
  onClose,
  relics,
  onPick,
  lockClose = false,
  title = 'Choose a trinket:',
  secondaryActionLabel,
  onSecondaryAction,
}) {
  // Prevent the "tap that opened the chest" from immediately selecting the first relic on mobile.
  const [armed, setArmed] = React.useState(false);

  const handleSecondaryAction = React.useCallback(() => {
    if (onSecondaryAction) {
      onSecondaryAction();
      return;
    }
    onClose?.();
  }, [onClose, onSecondaryAction]);

  const resolvedSecondaryActionLabel =
    typeof secondaryActionLabel === 'string' && secondaryActionLabel.trim().length > 0
      ? secondaryActionLabel
      : 'Skip this choice';

  const showSecondaryAction = Boolean(onSecondaryAction || onClose);

  React.useEffect(() => {
    if (!open) return;
    setArmed(false);
    const t = window.setTimeout(() => setArmed(true), 220);
    return () => window.clearTimeout(t);
  }, [open]);

  React.useEffect(() => {
    if (!open || lockClose) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, lockClose, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={lockClose ? undefined : onClose}
        >
          <motion.div
            className="relative z-[1] max-h-[92vh] w-[760px] max-w-[94vw] overflow-y-auto bg-black/80 px-6 pb-8 pt-5 text-white shadow-[0_20px_60px_rgba(0,0,0,0.72)] sm:px-8 sm:pb-10 sm:pt-6"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <CornerDetail className="left-4 top-4" rotationClass="rotate-180" />
            <CornerDetail className="right-4 top-4" rotationClass="-rotate-90" />
            <CornerDetail className="left-4 bottom-4" rotationClass="rotate-90" />
            <CornerDetail className="right-4 bottom-4" />

            <div className="relative z-[1] mx-auto flex w-full max-w-[720px] flex-col pt-8 sm:pt-10">
              <h2
                className="mb-5 text-center font-karma text-[18px] font-semibold text-white sm:mb-6 sm:text-[20px]"
                style={{ fontFamily: 'Karma, serif' }}
              >
                {title}
              </h2>

              <div className="space-y-3">
                {relics.map((relic, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => {
                      if (!armed) return;
                      onPick(relic);
                    }}
                    className="flex w-full items-start gap-4 rounded-[22px] border border-white/10 bg-black/90 px-4 py-3 text-left transition hover:bg-white/[0.06] active:scale-[0.995]"
                  >
                    <img
                      src={relic.image}
                      alt={relic.name}
                      className="h-20 w-20 shrink-0 object-contain sm:h-24 sm:w-24"
                    />

                    <div className="min-w-0 flex-1 pt-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <div
                          className="break-words font-karma text-[18px] font-semibold leading-tight text-white sm:text-[20px]"
                          style={{ fontFamily: 'Karma, serif' }}
                        >
                          {relic.name}
                        </div>

                        {!!relic.rarity && (
                          <span className="shrink-0 rounded-full border border-white/12 bg-white/[0.04] px-3 py-1 text-[11px] leading-none tracking-wide text-white/70 sm:text-[12px]">
                            {formatRarity(relic.rarity)}
                          </span>
                        )}
                      </div>

                      <div className="mt-2 break-words whitespace-normal text-[15px] leading-[1.35] text-white/80 sm:text-[16px]">
                        {relic.description}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {showSecondaryAction && (
                <div className="mt-7 flex justify-center pb-2">
                  <button
                    type="button"
                    onClick={handleSecondaryAction}
                    className="relative h-[68px] w-[250px] appearance-none border-0 bg-transparent p-0 text-white shadow-none outline-none transition hover:opacity-90 active:scale-[0.99]"
                    style={{ fontFamily: 'Karma, serif' }}
                  >
                    <img
                      src={loreButton}
                      alt=""
                      aria-hidden="true"
                      draggable={false}
                      className="pointer-events-none absolute inset-0 h-full w-full select-none object-contain"
                    />
                    <span className="relative z-[1] flex h-full w-full translate-y-[2px] items-center justify-center text-[18px]">
                      {resolvedSecondaryActionLabel}
                    </span>
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
